import {css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  GetStylesFunction,
  IconHeaderWithSearch,
  LightSplitLayout,
  LoadingSpinner,
  MaximizeButton,
  mixins,
  omitNullsy,
  useLocationQuery,
} from '@wallaby-ui/core';
import {
  LightSelectedFilters,
  MerieSchema,
  TableSorting,
  useFormatters,
  usePermissions,
  UserFilterDto,
  useUserFilters,
} from '@wallaby-ui/document';
import {isEqual} from 'lodash';
import * as React from 'react';
import {useMemo, useState} from 'react';
import {Helmet} from 'react-helmet';

import {
  DEFAULT_HOSPITAL_REPORT_FILTERS,
  DEFAULT_HOSPITAL_REPORT_PAGING,
  DEFAULT_HOSPITAL_REPORT_SORTING,
  FILTER_TABLE_CHART_LAYOUT_SPLIT_CONFIG,
  type FiltersData,
  HOSPITAL_CHARTS_LAYOUT_SPLIT_CONFIG,
  HospitalReportChart,
  HospitalReportFilters,
  HospitalReportTable,
  type InterventionOverviewReportFilter,
  InterventionOverviewReportFilterDepartmentDetailType,
  normalizeInterventionOverviewReportFiltersForCompare,
  ReportType,
  useReportData,
} from './meriehospitalreports';
import {useHospitalOptions} from './meriehospitalreports/useHospitalOptions';
import {useHospitalReportExport} from './meriehospitalreports/useHospitalReportExport';
import {useMaximizeWrapper} from './meriehospitalreports/useMaximizeWrapper';

type Props = {
  config: ConfigObjectType;
};

export const MerieHospitalReportLayout: React.FC<Props> = (props) => {
  const {config} = props;
  const storageIdPrefix = '/merieHospitalReportLayout';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);

  const {firstHospitalOption} = useHospitalOptions(config);
  const defaultReportFilters: FiltersData['report'] = useMemo(() => {
    return DEFAULT_HOSPITAL_REPORT_FILTERS(firstHospitalOption?.id);
  }, [firstHospitalOption?.id]);

  const filters: FiltersData = useMemo(
    () => ({
      report: query?.report ?? defaultReportFilters,
      reportTableSorting: query?.reportTableSorting ?? DEFAULT_HOSPITAL_REPORT_SORTING,
      reportTablePaging: query?.reportTablePaging ?? DEFAULT_HOSPITAL_REPORT_PAGING,
      savedFiltersOpen: query?.savedFiltersOpen ?? true,
    }),
    [query, defaultReportFilters]
  );

  const component = 'hospitalReport';
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({report: newFilters}),
  });

  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;

  return (
    <MerieHospitalReportLayoutComponents
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
      defaultReportFilters={defaultReportFilters}
    />
  );
};

const computeDepartmentDetail = (organizationalLevelType: MerieSchema.components['schemas']['OrganizationLevelTypeEnum'] | undefined) => {
  if (!organizationalLevelType) return null;
  return organizationalLevelType === 'PARENT_DEPARTMENT' ? 'DEPARTMENT' : 'UNIT';
};

type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Partial<FiltersData>) => void;
  defaultReportFilters?: FiltersData['report'];
};

const MerieHospitalReportLayoutComponents: React.FC<InnerProps> = ({
  config,
  storageIdPrefix,
  component,
  userFiltersLoading,
  addedUserFilters,
  userFilters,
  changeUserFilter,
  filters,
  changeFilters,
  defaultReportFilters,
}) => {
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const {isLoading: permissionsLoading} = usePermissions(config);
  const formatters = useFormatters();
  const {t} = formatters;

  const [departmentDetail, setDepartmentDetail] = useState<InterventionOverviewReportFilterDepartmentDetailType | null>(
    computeDepartmentDetail(filters.report.organizationLevelType)
  );

  const reportUrl = `${config.service.url.interventionStatisticsReport}`;
  const {
    tableData,
    chartData,
    isLoading: isReportDataLoading,
  } = useReportData({
    reportUrl,
    filters: filters.report,
    reportType: ReportType.DEPARTMENT,
  });

  const {handleXlsxExport, warningDialog} = useHospitalReportExport(filters.report, config, tableData?.length ?? 0);

  const handleHeaderLabelTranslation = () => {
    const organizationalLevelType = filters.report.organizationLevelType === 'PARENT_DEPARTMENT' ? 'DEPARTMENT' : 'UNIT';
    if (organizationalLevelType === 'DEPARTMENT') {
      return 'hospital.report.table.name.department';
    } else {
      return 'hospital.report.table.name.unit';
    }
  };

  const handleFiltersChange = (newFilters: Partial<InterventionOverviewReportFilter>) => {
    changeFilters({
      report: {...filters.report, ...newFilters},
    });
  };

  const handleSortingChange = (sorting: TableSorting) => {
    changeFilters({reportTableSorting: sorting});
  };

  const onToggleSavedFiltersOpen = () => {
    changeFilters({savedFiltersOpen: !filters.savedFiltersOpen});
  };

  const handleResetFilters = () => {
    changeFilters({report: defaultReportFilters});
  };

  const handleResetSorting = () => {
    changeFilters({reportTableSorting: DEFAULT_HOSPITAL_REPORT_SORTING});
  };

  const handleSetFilter = (newFilter: InterventionOverviewReportFilter) => {
    changeFilters({report: newFilter});
  };

  const {
    maximized: tableMaximized,
    toggleMaximized: tableToggleMaximized,
    MaximizeWrapper: TableMaximizeWrapper,
  } = useMaximizeWrapper('report-table');

  if (permissionsLoading) {
    return <LoadingSpinner isVisible text='' />;
  }

  const hasFilters = !isEqual(
    normalizeInterventionOverviewReportFiltersForCompare(omitNullsy(filters.report) as any),
    normalizeInterventionOverviewReportFiltersForCompare(omitNullsy(defaultReportFilters) as any)
  );
  const mainSplitLayout = FILTER_TABLE_CHART_LAYOUT_SPLIT_CONFIG('/LightSplitLayout.MerieHospitalReportLayout');
  const chartSplitLayout = HOSPITAL_CHARTS_LAYOUT_SPLIT_CONFIG('/LightSplitLayout.MerieHospitalReportLayout.Charts');

  return (
    <div css={muiStyles.contentWrapper}>
      <Helmet title={`${t('reports.list.pageTitle')} | ${t('navigation.route.hospitalReportMerie')}`} />
      <LightSplitLayout
        initSizes={mainSplitLayout[0].sizes}
        minSizes={mainSplitLayout[0].minSize}
        storageId={mainSplitLayout[0].layoutId}
        isVertical
        gutterSize={18}
        noGrow={[!filters.savedFiltersOpen, false, false]}
        autoFitFilterRows={{enabled: filters.savedFiltersOpen}}
        hidden={[false, false, false]}
      >
        <div data-name='report-filters' css={muiStyles.filtersContentBox}>
          <HospitalReportFilters
            reportType={ReportType.DEPARTMENT}
            filters={filters.report}
            onFiltersChange={handleFiltersChange}
            savedFiltersOpen={filters.savedFiltersOpen}
            onToggleSavedFilters={onToggleSavedFiltersOpen}
            storageIdPrefix={storageIdPrefix}
            component={component}
            userFiltersLoading={userFiltersLoading}
            addedUserFilters={addedUserFilters}
            userFilters={userFilters}
            changeUserFilter={changeUserFilter}
            config={config}
            onSetFilter={handleSetFilter}
            departmentDetail={departmentDetail}
            setDepartmentDetail={setDepartmentDetail}
            defaultReportFilters={defaultReportFilters}
            hasActiveFilter={hasFilters}
          />
        </div>
        <TableMaximizeWrapper>
          <IconHeaderWithSearch
            iconName='listIconMultiColor'
            label={handleHeaderLabelTranslation()}
            useFixedViewBox='0 -1 16 14'
            iconCss={muiStyles.headerIcon}
            childrenWrapperStyle={{gap: 6}}
          >
            <LightSelectedFilters
              align='flex-start'
              marginLeft='8px'
              hasFilters={hasFilters}
              handleResetFilters={handleResetFilters}
              hasSorting={!isEqual(filters.reportTableSorting, DEFAULT_HOSPITAL_REPORT_SORTING)}
              handleResetSorting={handleResetSorting}
              hasXlsxExport
              handleXlsxExport={handleXlsxExport}
            />
            <MaximizeButton maximized={tableMaximized} toggleMaximized={tableToggleMaximized} />
          </IconHeaderWithSearch>
          <HospitalReportTable
            reportType={ReportType.DEPARTMENT}
            data={tableData}
            isLoading={isReportDataLoading}
            sorting={filters.reportTableSorting}
            onSortingChange={handleSortingChange}
            filters={filters}
            storageIdPrefix={storageIdPrefix}
          />
          {warningDialog}
        </TableMaximizeWrapper>
        <div data-name='report-charts' css={muiStyles.chartsContentBox}>
          <LightSplitLayout
            initSizes={chartSplitLayout[0].sizes}
            minSizes={chartSplitLayout[0].minSize}
            storageId={chartSplitLayout[0].layoutId}
            isVertical={false}
            gutterSize={18}
            noGrow={[false, false]}
            hidden={[false, false]}
          >
            <HospitalReportChart
              title='hospital.network.report.charts.absolute.name'
              data={chartData}
              isLoading={isReportDataLoading}
              dataKeys={['measureCountHigh', 'measureCountMedium', 'measureCountLow']}
              chartType='absolute'
              fractionDigits={1}
              dataName={'left-chart'}
            />
            <HospitalReportChart
              title='hospital.network.report.charts.caredays.name'
              data={chartData}
              isLoading={isReportDataLoading}
              dataKeys={['measureCountHighPer1000HospDays', 'measureCountMediumPer1000HospDays', 'measureCountLowPer1000HospDays']}
              chartType='caredays'
              fractionDigits={1}
              dataName={'right-chart'}
            />
          </LightSplitLayout>
        </div>
      </LightSplitLayout>
    </div>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
  `,
  contentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    background: #ffffff;
    padding: 16px;
    row-gap: 16px;
    overflow-y: auto;
  `,
  /* Filters card only: no bottom padding, so the split's autoFit "half of the 3rd filter row" peek is
     clipped right AT the divider instead of 16px above it (the padding pushed the clip line up). */
  filtersContentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    background: #ffffff;
    padding: 16px 16px 0 16px;
    row-gap: 16px;
    overflow-y: auto;
  `,
  chartsContentBox: css`
    ${mixins.contentBox};
    padding: 0;
    row-gap: 16px;
    overflow-y: auto;
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
});
